import { defineMessages } from 'react-intl-next';

export const codeSuggestionCardMessages = defineMessages({
  suggestionCardTitle: {
    id: 'frontbucket.codeSuggestions.suggestionCard.title',
    description: 'Title for the suggestion card',
    defaultMessage: 'Suggested change',
  },
  appliedLozenge: {
    id: 'frontbucket.codeSuggestions.suggestionCard.appliedLozenge',
    description: 'Text indicating that the suggestion has been applied',
    defaultMessage: 'Applied',
  },
  applyButtonText: {
    id: 'frontbucket.codeSuggestions.suggestionCard.applyButtonText',
    description: 'Text for the apply suggestion button',
    defaultMessage: 'Apply suggestion',
  },
  applyButtonTooltipTextCanApply: {
    id: 'frontbucket.codeSuggestions.suggestionCard.applyButtonTooltipText.canApply',
    description: 'Tooltip text when the suggestion can be applied',
    defaultMessage: 'Create commit with this suggestion',
  },
  applyButtonTooltipTextCannotApplyFallbackText: {
    id: 'frontbucket.codeSuggestions.suggestionCard.applyButtonTooltipText.cannotApplyFallbackText',
    description: 'Fallback tooltip text when the suggestion cannot be applied',
    defaultMessage: 'Unable to apply suggestion.',
  },
  applyButtonTooltipTextNotPermitted: {
    id: 'frontbucket.codeSuggestions.suggestionCard.applyButtonTooltipText.notPermitted',
    description: 'Tooltip text when user lacks permission to apply suggestion',
    defaultMessage:
      "We can't apply this suggestion because you don't have permission to make changes to the source branch.",
  },
  applyButtonTooltipTextPendingMerge: {
    id: 'frontbucket.codeSuggestions.suggestionCard.applyButtonTooltipText.pendingMerge',
    description: 'Tooltip text when there is a pending merge',
    defaultMessage:
      "You can't apply suggestions while there is a pending merge.",
  },
  applyButtonTooltipTextInvalidPrState: {
    id: 'frontbucket.codeSuggestions.suggestionCard.applyButtonTooltipText.invalidPrState',
    description:
      'Tooltip text when suggestion is in an invalid pull request state',
    defaultMessage: 'You can only apply suggestions in open pull requests.',
  },
  applyButtonTooltipTextInvalidCommitComment: {
    id: 'frontbucket.codeSuggestions.suggestionCard.applyButtonTooltipText.invalidCommitComment',
    description:
      'Tooltip text when suggestion is not in a pull request comment',
    defaultMessage:
      'Only pull request comments can have their suggestions applied.',
  },
  applyButtonTooltipTextInvalidOrOrphanedComment: {
    id: 'frontbucket.codeSuggestions.suggestionCard.applyButtonTooltipText.invalidOrOrphanedComment',
    description:
      'Tooltip text when suggestion is in an outdated or orphaned comment',
    defaultMessage: "You can't apply suggestions in outdated comments.",
  },
  applyButtonTooltipTextInvalidCommentState: {
    id: 'frontbucket.codeSuggestions.suggestionCard.applyButtonTooltipText.invalidCommentState',
    description: 'Tooltip text when suggestion is in a pending comment',
    defaultMessage: "You can't apply suggestions on pending comments.",
  },
  applyButtonTooltipTextInvalidRemovedLine: {
    id: 'frontbucket.codeSuggestions.suggestionCard.applyButtonTooltipText.invalidRemovedLine',
    description: 'Tooltip text when suggestion is on a removed line',
    defaultMessage: "You can't apply suggestions on removed lines.",
  },
  applyButtonTooltipTextInvalidDiffType: {
    id: 'frontbucket.codeSuggestions.suggestionCard.applyButtonTooltipText.invalidDiffType',
    description: 'Tooltip text when suggestion is in an iterative review diff',
    defaultMessage:
      "You can't apply suggestions in comments that are in iterative pull request diffs.",
  },
  applyButtonTooltipTextRedundantSuggestion: {
    id: 'frontbucket.codeSuggestions.suggestionCard.applyButtonTooltipText.redundantSuggestion',
    description:
      'Tooltip text when the suggestion is redundant because it is identical to the original line',
    defaultMessage:
      "You can't apply suggestions that are identical to the original line.",
  },
});

export const commitSuggestionModalMessages = defineMessages({
  fieldErrorText: {
    id: 'frontbucket.codeSuggestions.commitSuggestionModal.fieldErrorText',
    description: 'Error message for commit message field',
    defaultMessage: 'Please enter a commit message',
  },
  commitModalTitle: {
    id: 'frontbucket.codeSuggestions.commitSuggestionModal.commitModalTitle',
    description: 'Title for commit suggestion modal',
    defaultMessage: 'Apply suggestion',
  },
  cancelButton: {
    id: 'frontbucket.codeSuggestions.commitSuggestionModal.cancelButton',
    description: 'Text for cancel button in commit suggestion modal',
    defaultMessage: 'Cancel',
  },
  commitChangesButton: {
    id: 'frontbucket.codeSuggestions.commitSuggestionModal.commitChangesButton',
    description: 'Text for commit changes button in commit suggestion modal',
    defaultMessage: 'Commit changes',
  },
  defaultCommitMessage: {
    id: 'frontbucket.codeSuggestions.commitSuggestionModal.defaultCommitMessage',
    description: 'Default commit message for commit suggestion modal',
    defaultMessage: 'Applied suggestion to {filePath}',
  },
  commitMessageFieldLabel: {
    id: 'frontbucket.codeSuggestions.commitSuggestionModal.commitMessageFieldLabel',
    description: 'Label for commit message field',
    defaultMessage: 'Commit message',
  },
  commitDetailsPlaceholderText: {
    id: 'frontbucket.codeSuggestions.commitSuggestionModal.commitDetailsPlaceholderText',
    description: 'Placeholder text for commit details field',
    defaultMessage: 'Add a description of what you changed and why (optional)',
  },
});

export const codeSuggestionsIconMessages = defineMessages({
  iconLabel: {
    id: 'frontbucket.codeSuggestions.icon.label',
    description: 'Label for code suggestions icon',
    defaultMessage: 'Code Suggestions',
  },
});

export const commitResponseFlagMessages = defineMessages({
  successTitle: {
    id: 'frontbucket.codeSuggestions.successFlag.title',
    description: 'Title for committed code suggestion success flag',
    defaultMessage: 'Code suggestion applied successfully',
  },
  successDescription: {
    id: 'frontbucket.codeSuggestions.successFlag.description',
    description: 'Description for committed code suggestion success flag',
    defaultMessage: 'Your changes will be reflected in the diff momentarily.',
  },
  errorTitle: {
    id: 'frontbucket.codeSuggestions.errorFlag.title',
    description: 'Title for committed code suggestion error flag',
    defaultMessage: 'Failed to apply code suggestion',
  },
  errorDefaultDescription: {
    id: 'frontbucket.codeSuggestions.errorFlag.description',
    description: 'Default description for committed code suggestion error flag',
    defaultMessage: 'An unknown error occurred.',
  },
  errorInvalidParameters: {
    id: 'frontbucket.codeSuggestions.errorFlag.invalidParameters',
    description:
      'Description for committed code suggestion error flag when parameters are invalid',
    defaultMessage: 'Invalid parameters',
  },
});

export const codeSuggestionsEditModeMessages = defineMessages({
  title: {
    id: 'frontbucket.codeSuggestions.editMode.title',
    description: 'Title for code suggestions edit mode',
    defaultMessage: 'Suggest Code',
  },
  description: {
    id: 'frontbucket.codeSuggestions.editMode.description',
    description: 'Description for code suggestions edit mode',
    defaultMessage: 'Make a code suggestion',
  },
});
